import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckCircle, XCircle, MapPin, PencilSimple, Warning, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function StaticValue({ value, mono = false }) {
    return (
        <div className={`text-sm font-semibold text-slate-900 break-words ${mono ? "hash-mono" : ""}`}>
            {value || <span className="text-slate-400 italic font-normal">Not provided</span>}
        </div>
    );
}

function EditableField({ label, value, type = "text", onSave, testid, multiline = false, hint }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const [challengePin, setChallengePin] = useState("");
    useEffect(() => { setDraft(value || ""); setChallengePin(""); }, [value]);
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
                {!editing && (
                    <button
                        type="button"
                        className="text-[10px] text-civic-600 font-semibold hover:underline flex items-center gap-1"
                        onClick={() => setEditing(true)}
                        data-testid={`${testid}-edit-btn`}
                    >
                        <PencilSimple size={10} weight="bold" /> Edit
                    </button>
                )}
            </div>
            {editing ? (
                <div className="space-y-1.5">
                    {multiline ? (
                        <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${testid}-input`} className="text-sm" />
                    ) : (
                        <Input type={type} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${testid}-input`} className="h-8 text-sm" />
                    )}
                    <Input type="password" value={challengePin} onChange={(e) => setChallengePin(e.target.value)} placeholder="Enter your PIN to authorize" data-testid={`${testid}-pin`} className="h-8 text-sm font-mono" />
                    {hint && <p className="text-[10px] text-slate-500 leading-snug">{hint}</p>}
                    <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs bg-civic-600 hover:bg-civic-700 text-white"
                            onClick={async () => {
                                if (!challengePin) { toast.error("Enter your PIN to authorize the change"); return; }
                                const ok = await onSave(draft, challengePin);
                                if (ok) { setEditing(false); setChallengePin(""); }
                            }}
                            data-testid={`${testid}-save-btn`}
                        >Save</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => { setDraft(value || ""); setChallengePin(""); setEditing(false); }}
                            data-testid={`${testid}-cancel-btn`}
                        >Cancel</Button>
                    </div>
                </div>
            ) : (
                <StaticValue value={value} />
            )}
        </div>
    );
}

export default function ConfirmInfoDialog({ profile, election, open, onConfirm, onExit, onProfileChange }) {
    if (!profile) return null;

    const saveField = (field) => async (newValue, currentPin) => {
        try {
            const { data } = await api.put("/voter/profile", { current_pin: currentPin, [field]: newValue });
            onProfileChange?.(data);
            toast.success(`${field} updated · the voting authority has been notified`);
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail || `Could not update ${field}`);
            return false;
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onExit?.()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-5 gap-3" data-testid="confirm-info-dialog">
                <DialogHeader className="space-y-1">
                    <DialogTitle className="font-heading text-xl">Confirm Your Information</DialogTitle>
                    <DialogDescription className="text-xs">
                        Please review and confirm that the information below is correct before proceeding.
                    </DialogDescription>
                </DialogHeader>

                {/* Block 1 — read-only identity */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
                        <Field label="Full Name" testid="confirm-name">
                            <StaticValue value={profile.name} />
                        </Field>
                        <Field label="Voter ID" testid="confirm-voter-id">
                            <StaticValue value={profile.voter_id} mono />
                        </Field>
                        <Field label="Precinct" testid="confirm-precinct">
                            <StaticValue value={profile.precinct} mono />
                        </Field>
                        <Field label="Date of Birth · Age" testid="confirm-dob-age">
                            <StaticValue value={(() => {
                                const dobStr = profile.dob ? new Date(profile.dob + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;
                                const ageStr = profile.age != null ? `${profile.age} yrs` : null;
                                if (dobStr && ageStr) return `${dobStr}  ·  ${ageStr}`;
                                return dobStr || ageStr || null;
                            })()} />
                        </Field>
                        <EditableField
                            label="Email"
                            value={profile.email || ""}
                            type="email"
                            onSave={saveField("email")}
                            testid="confirm-email"
                            hint="Pass empty to remove. Authority will email a verification link before this is used for ballot notifications."
                        />
                        <EditableField
                            label="Mobile Number"
                            value={profile.mobile || ""}
                            type="tel"
                            onSave={saveField("mobile")}
                            testid="confirm-mobile"
                            hint="Authority will send a one-time SMS to verify before reminders are enabled."
                        />
                    </div>
                </div>

                {/* Block 2 — address */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                    <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                        <MapPin size={12} weight="duotone" /> Registered Address
                    </div>
                    <EditableField
                        label=""
                        value={profile.address || ""}
                        multiline
                        onSave={saveField("address")}
                        testid="confirm-address"
                        hint="P.O. Boxes are not accepted. New addresses are validated against the county roll; precinct may change after verification."
                    />
                </div>

                {/* Verification & legal notice */}
                <div className="rounded-md border border-civic-200 bg-civic-50 p-3 text-xs text-slate-800 leading-snug" data-testid="legal-notice">
                    <div className="flex items-start gap-2">
                        <Info size={14} weight="fill" className="text-civic-600 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold">How changes are verified:</span> Updates to email, mobile, or address are queued for verification by your election authority ({election?.authority || profile.authority || "the county"}). Email and SMS changes trigger a confirmation message; address changes are cross-referenced against the county registration database. Your ballot may proceed using current information.
                        </div>
                    </div>
                    <div className="flex items-start gap-2 mt-2 pt-2 border-t border-civic-200">
                        <Warning size={14} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold text-red-700">Legal notice:</span> Knowingly providing false information, impersonating another voter, or submitting more than one ballot per election violates city, state, and federal election law. Penalties under <span className="hash-mono">52 U.S.C. § 20511</span> include up to <span className="font-semibold">5 years imprisonment</span> and <span className="font-semibold">$10,000</span> in fines, plus state-level felonies.
                        </div>
                    </div>
                </div>

                {/* Footer actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    <Button
                        type="button"
                        onClick={onExit}
                        className="h-10 bg-red-600 hover:bg-red-700 text-white text-sm whitespace-normal leading-tight order-2 sm:order-1"
                        data-testid="confirm-exit-btn"
                    >
                        <XCircle size={14} className="mr-1.5 shrink-0" weight="fill" /> Exit
                    </Button>
                    <Button
                        type="button"
                        onClick={onConfirm}
                        className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-sm whitespace-normal leading-tight order-1 sm:order-2"
                        data-testid="confirm-proceed-btn"
                    >
                        <CheckCircle size={14} className="mr-1.5 shrink-0" weight="fill" /> <span className="sm:hidden">Confirm</span><span className="hidden sm:inline">Confirm Information is Correct</span>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
